import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Save, Loader2, ExternalLink, Briefcase, RotateCcw } from "lucide-react";
import { Link } from "react-router-dom";

const FIELDS = [
  { k: "hero_eyebrow", label: "Hero eyebrow", hint: "Small text above the hero title", type: "input" },
  { k: "hero_title_line1", label: "Hero title — line 1", hint: "Displayed in white", type: "input" },
  { k: "hero_title_line2", label: "Hero title — line 2", hint: "Displayed in orange", type: "input" },
  { k: "hero_image_url", label: "Hero background image URL", hint: "Wide landscape photo works best", type: "input" },
  { k: "job_tag", label: "Job posting tag", hint: "The 'Now Hiring' pill above the job title", type: "input" },
  { k: "job_title", label: "Job posting title", hint: "Main heading in the black job card", type: "input" },
  { k: "job_blurb", label: "Job posting blurb", hint: "The paragraph in the black job card — line breaks preserved", type: "textarea", rows: 5 },
  { k: "intro_note", label: "Intro note above the form", hint: "The short instruction that welcomes the applicant", type: "textarea", rows: 3 },
];

export default function CareersContent() {
  const [form, setForm] = useState(null);
  const [initial, setInitial] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/site-content/careers")
      .then((r) => { setForm(r.data); setInitial(r.data); })
      .catch(() => toast.error("Couldn't load careers content"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const dirty = !!(form && initial) && FIELDS.some(({ k }) => (form[k] ?? "") !== (initial[k] ?? ""));

  const save = async () => {
    if (!form) return;
    setSaving(true);
    try {
      const patch = {};
      for (const { k } of FIELDS) patch[k] = form[k] ?? "";
      const { data } = await api.put("/site-content/careers", patch);
      setForm(data); setInitial(data);
      toast.success("Careers page content updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const revert = () => { setForm(initial); toast("Reverted to last-saved values"); };

  if (loading || !form) {
    return <div className="p-10 text-neutral-500">Loading…</div>;
  }

  return (
    <div className="p-8 lg:p-10">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <div>
          <p className="eyebrow mb-2">Content</p>
          <h1 className="font-display font-bold text-3xl tracking-tight" data-testid="admin-careers-content-title">Careers Page Content</h1>
          <p className="text-sm text-neutral-500 mt-1">Edit the wording on the public Careers page — no redeploy needed.</p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/careers" target="_blank" rel="noreferrer">
            <Button variant="outline" className="rounded-full" data-testid="cc-preview">
              <ExternalLink className="w-4 h-4 mr-1.5"/> Preview
            </Button>
          </Link>
          {dirty && (
            <Button variant="outline" onClick={revert} className="rounded-full" data-testid="cc-revert">
              <RotateCcw className="w-4 h-4 mr-1.5"/> Revert
            </Button>
          )}
          <Button
            onClick={save}
            disabled={saving || !dirty}
            className="rounded-full bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90"
            data-testid="cc-save"
          >
            {saving ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin"/>Saving…</> : <><Save className="w-4 h-4 mr-1.5"/>Save changes</>}
          </Button>
        </div>
      </div>

      <div className="grid xl:grid-cols-5 gap-8">
        {/* Editor */}
        <div className="xl:col-span-3 bg-white rounded-2xl border border-neutral-200/70 p-6 space-y-5">
          {FIELDS.map(({ k, label, hint, type, rows }) => (
            <div key={k} data-testid={`cc-field-${k}`}>
              <Label className="text-sm font-medium">{label}</Label>
              {type === "textarea" ? (
                <Textarea
                  rows={rows || 4}
                  value={form[k] ?? ""}
                  onChange={(e) => set(k, e.target.value)}
                  className="mt-1"
                  data-testid={`cc-input-${k}`}
                />
              ) : (
                <Input
                  value={form[k] ?? ""}
                  onChange={(e) => set(k, e.target.value)}
                  className="mt-1"
                  data-testid={`cc-input-${k}`}
                />
              )}
              <p className="text-xs text-neutral-500 mt-1">{hint}</p>
            </div>
          ))}
        </div>

        {/* Live preview */}
        <div className="xl:col-span-2 space-y-4">
          <div className="text-xs uppercase tracking-wider text-neutral-500 font-bold">Live preview</div>

          {/* Hero preview */}
          <div className="relative rounded-2xl overflow-hidden h-56 border border-neutral-200/70" data-testid="cc-preview-hero">
            <img src={form.hero_image_url} alt="" className="absolute inset-0 w-full h-full object-cover"/>
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-transparent"/>
            <div className="absolute bottom-6 left-6 right-6">
              <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-white/80 mb-1.5">{form.hero_eyebrow}</div>
              <div className="font-display font-bold text-3xl text-white leading-tight">
                {form.hero_title_line1} <span className="text-[hsl(var(--primary))]">{form.hero_title_line2}</span>
              </div>
            </div>
          </div>

          {/* Job card preview */}
          <div className="rounded-2xl bg-neutral-950 text-white p-5 relative overflow-hidden" data-testid="cc-preview-job">
            <div className="absolute -right-6 -top-6 w-24 h-24 rounded-full bg-[hsl(var(--primary))]/20"/>
            <div className="relative">
              <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-[hsl(var(--primary))]">{form.job_tag}</span>
              <h3 className="font-display font-bold text-xl mt-1.5">{form.job_title}</h3>
              <p className="text-xs text-neutral-300 mt-2 leading-relaxed whitespace-pre-line line-clamp-6">{form.job_blurb}</p>
            </div>
          </div>

          {/* Intro preview */}
          <div className="rounded-2xl bg-white border border-neutral-200/70 p-5 flex gap-3" data-testid="cc-preview-intro">
            <div className="w-8 h-8 rounded-full bg-[hsl(var(--primary))]/10 flex items-center justify-center shrink-0">
              <Briefcase className="w-4 h-4 text-[hsl(var(--primary))]"/>
            </div>
            <p className="text-sm text-neutral-700 leading-relaxed whitespace-pre-line">{form.intro_note}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
